/**
 * Returns true if the user is authenticated (user exists and access token is present).
 * Use this in all global components that should only mount for authenticated users.
 */
export function isTrulyAuthenticated(user: unknown): boolean {
  return !!user && !!sessionStorage.getItem("spark_access_token");
}

/**
 * Returns true if a session can still be RESUMED — which is a weaker and
 * different question from whether one is live right now.
 *
 * The access token lives in sessionStorage and dies with the tab. The refresh
 * token lives in localStorage and does not. So the ordinary returning
 * visitor — closed the tab yesterday, opens it today — has no access token at
 * boot and a perfectly good session, which `useTokenRefresh` exchanges for a
 * new one as soon as the app mounts.
 *
 * Use this only for what is rendered BEFORE that exchange can happen, where
 * the choice is between a correct guess and a wrong one. Anything that gates
 * access, or mounts on the strength of being signed in, wants
 * `isTrulyAuthenticated` instead: a resumable session is not an authenticated
 * one until the refresh actually succeeds.
 */
export function hasResumableSession(user: unknown): boolean {
  if (!user) return false;
  return (
    !!sessionStorage.getItem("spark_access_token") ||
    !!localStorage.getItem("spark_refresh_token")
  );
}
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

interface User {
  /** User ID (maps to userId from backend, or sub from Cognito) */
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  activePersonaId?: string;
  personas?: Array<{ personaId: string; name: string }>;
  /** Subscription tier — populated from backend; defaults to 'free' when absent. */
  subscriptionTier?: "free" | "pro" | "team" | "enterprise";
  settings?: {
    timezone?: string;
    emailNotifications?: boolean;
  };
  /** Set once the one-time "tune it" panel after the first draft is dismissed. */
  firstDraftNudgeSeen?: boolean;
  /** Which Home dashboard the person uses; absent means classic. */
  dashboard?: "classic" | "new";
  /** Account-level answers from the questions screen. */
  signup?: { teamSize?: string; referralSource?: string };
  /** Onboarding progress — null/undefined means not started. */
  onboarding?: {
    completedSteps: string[];
    currentStep: string;
    skippedSteps: string[];
    completedAt?: string;
    walkthroughs?: Record<string, string[]>;
  } | null;
}

interface AuthState {
  /** Currently authenticated user (null if logged out) */
  user: User | null;
  /** Whether the auth state has been initialized (checked storage) */
  initialized: boolean;
  /** Full-screen loading gate shown right after a successful login */
  postLoginLoading: boolean;
  /** Whether the guided product tour should start after onboarding completes */
  tourAutoStartPending: boolean;

  /** Set the authenticated user */
  setUser: (user: User) => void;
  /** Clear auth state (logout) */
  clearUser: () => void;
  /** Mark initialization as complete */
  setInitialized: () => void;
  /** Show post-login loading screen */
  startPostLoginLoading: () => void;
  /** Hide post-login loading screen */
  finishPostLoginLoading: () => void;
  /** Set whether the guided product tour should auto-start */
  setTourAutoStartPending: (pending: boolean) => void;
  /** Consume and clear the pending guided product tour flag */
  consumeTourAutoStartPending: () => boolean;
  /** Update only the active persona ID */
  updateActivePersonaId: (id: string) => void;
}

/**
 * Auth store (Zustand).
 *
 * Persists user info to localStorage so we can hydrate on reload.
 * Tokens are stored separately in sessionStorage/localStorage
 * (see services/api.ts).
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        initialized: false,
        postLoginLoading: false,
        tourAutoStartPending: false,

        setUser: (user) => set({ user }),
        clearUser: () =>
          set({
            user: null,
            postLoginLoading: false,
            tourAutoStartPending: false,
          }),
        setInitialized: () => set({ initialized: true }),
        startPostLoginLoading: () => set({ postLoginLoading: true }),
        finishPostLoginLoading: () => set({ postLoginLoading: false }),
        setTourAutoStartPending: (pending) =>
          set({ tourAutoStartPending: pending }),
        consumeTourAutoStartPending: () => {
          const pending = get().tourAutoStartPending;
          if (pending) {
            set({ tourAutoStartPending: false });
          }
          return pending;
        },
        updateActivePersonaId: (id) =>
          set((state) => ({
            user: state.user ? { ...state.user, activePersonaId: id } : null,
          })),
      }),
      {
        name: "spark-auth",
        partialize: (state) => ({ user: state.user }),
      }
    ),
    { name: "auth" }
  )
);
